import React, { useState } from "react";
import { createRepairListing, updateRepairListing } from "../lib/repairListings";
import { uploadRepairListingPhoto } from "../lib/storage";
import { T } from "../theme";
import { Btn, Input, PhotoPicker, SectionTitle } from "../ui";

const SERVICE_TYPES = ["Splice Repair", "Foam Pad Replacement", "Belt Replacement", "Shell Repair", "Other"];

export default function CreateRepairListingScreen({ ownerUserId, onDone, onCancel }) {
  const [providerName, setProviderName] = useState("");
  const [services, setServices] = useState([]);
  const [priceRange, setPriceRange] = useState("");
  const [turnaroundTime, setTurnaroundTime] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleService = (s) => setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const save = async () => {
    setError("");
    if (!providerName.trim()) {
      setError("Enter a provider/business name.");
      return;
    }
    if (services.length === 0) {
      setError("Select at least one service you offer.");
      return;
    }
    if (!description.trim()) {
      setError("Add a description with how customers should contact you.");
      return;
    }
    setSaving(true);
    try {
      const docRef = await createRepairListing(ownerUserId, {
        providerName: providerName.trim(),
        services,
        priceRange: priceRange || null,
        turnaroundTime: turnaroundTime || null,
        description: description.trim(),
        photoUrls: [],
      });
      if (photos.length > 0) {
        const photoUrls = await Promise.all(photos.map((p) => uploadRepairListingPhoto(ownerUserId, docRef.id, p.file)));
        await updateRepairListing(docRef.id, { photoUrls });
      }
      onDone();
    } catch (e) {
      setError("Could not create this listing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PhotoPicker items={photos} onChange={setPhotos} max={5} label="Photos of Your Work" />

      <SectionTitle>Provider Details</SectionTitle>
      <Input label="Provider / Business Name *" value={providerName} onChange={(e) => setProviderName(e.target.value)} placeholder="e.g. Rick's Tape Repair" />

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Services Offered *</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SERVICE_TYPES.map((s) => (
            <button key={s} onClick={() => toggleService(s)} style={{ background: services.includes(s) ? T.accent : T.statBg, border: `1px solid ${services.includes(s) ? T.accent : T.border}`, color: services.includes(s) ? "#fff" : T.muted, padding: "6px 12px", borderRadius: 20, fontSize: 11, cursor: "pointer", fontFamily: T.font }}>{s}</button>
          ))}
        </div>
      </div>

      <Input label="Price Range" value={priceRange} onChange={(e) => setPriceRange(e.target.value)} placeholder="e.g. $10–$30 per repair" />
      <Input label="Typical Turnaround" value={turnaroundTime} onChange={(e) => setTurnaroundTime(e.target.value)} placeholder="e.g. 3–5 days" />

      {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <SectionTitle>Description &amp; Contact Info</SectionTitle>
      <div style={{ marginBottom: 10 }}>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your experience and how customers can reach you — there's no in-app messaging yet." style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "9px 11px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box", minHeight: 90, resize: "vertical" }} />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <Btn variant="outline" onClick={onCancel} style={{ flex: 1 }}>Cancel</Btn>
        <Btn disabled={saving} onClick={save} style={{ flex: 2 }}>{saving ? "Posting…" : "Post Provider Listing"}</Btn>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
